import { FormIcon } from '../FormIcon.jsx';
import { ClearableSelect, Field, ListInput, Toggle } from './controls.jsx';
import { SearchableSelect } from '../../components/SearchableSelect.jsx';

const TRIGGERS = [
  { id: 'submitted', label: 'When a submission is received' },
  { id: 'reviewed', label: 'When a submission is marked reviewed' },
];

const ACTIONS = [
  { id: 'create_task', label: 'Create a project task', icon: 'checkbox' },
  { id: 'send_email', label: 'Send an email', icon: 'email' },
  { id: 'notify_team', label: 'Alert the team (app + Teams)', icon: 'bell' },
  { id: 'webhook', label: 'Send a webhook', icon: 'code' },
  { id: 'ghl_tags', label: 'Apply GoHighLevel tags', icon: 'integrations' },
];

function newRule() {
  return {
    id: Math.random().toString(36).slice(2, 12),
    name: '',
    enabled: true,
    trigger: 'submitted',
    action: 'create_task',
    config: { title: 'Follow up: {{form_name}}', assignee_id: null, due_in_days: 2, to: [], subject: '', body: '', url: '', tags: [] },
  };
}

function Builtins({ draft, goSettings }) {
  const s = draft.settings;
  const items = [
    s.notifications.notify_team && 'In-app alert to the team',
    s.notifications.team_emails.length && `Email to ${s.notifications.team_emails.join(', ')}`,
    s.notifications.client_confirmation && 'Confirmation email to the respondent',
    s.integrations.create_task && `Task: “${s.integrations.task_title}”`,
    s.integrations.ghl_upsert_contact && `GHL contact upsert${s.integrations.ghl_tags.length ? ` + tags ${s.integrations.ghl_tags.join(', ')}` : ''}`,
    (draft.audience === 'client' || draft.audience === 'project') && s.portal.remind_if_incomplete && draft.due_date && `Client reminders ${s.portal.reminder_days} day(s) before the due date`,
  ].filter(Boolean);

  return (
    <div className="fm-card fm-auto-builtins">
      <h3>Already on from Settings</h3>
      {items.length ? (
        <ul>
          {items.map((t) => (
            <li key={t}>
              <FormIcon name="check" size={15} /> {t}
            </li>
          ))}
        </ul>
      ) : (
        <p className="fm-hint">No built-in actions are enabled.</p>
      )}
      <button type="button" className="fm-btn fm-btn--sm" onClick={goSettings}>
        Edit in Settings
      </button>
    </div>
  );
}

export function FormAutomationsTab({ draft, patch, options, goSettings }) {
  const rules = draft.automations || [];
  const setRules = (updater) => patch((cur) => ({ automations: updater(cur.automations || []) }));
  const update = (id, changes) => setRules((list) => list.map((r) => (r.id === id ? { ...r, ...changes } : r)));
  const updateConfig = (id, changes) => setRules((list) => list.map((r) => (r.id === id ? { ...r, config: { ...r.config, ...changes } } : r)));

  return (
    <div className="fm-automations">
      <section className="fm-auto-main">
        <header className="fm-auto-head">
          <div>
            <h3>Automations</h3>
            <p>Run actions automatically when someone submits this form or when your team reviews a submission.</p>
          </div>
          <button type="button" className="fm-btn fm-btn--blue" onClick={() => setRules((list) => [...list, newRule()])}>
            <FormIcon name="plus" size={16} /> Add automation
          </button>
        </header>

        {!rules.length ? (
          <div className="fm-card fm-empty">
            <FormIcon name="zap" size={28} />
            <p>No automations yet. Add one to create tasks, send emails or call a webhook.</p>
          </div>
        ) : null}

        {rules.map((rule, index) => {
          const action = ACTIONS.find((a) => a.id === rule.action) || ACTIONS[0];
          return (
            <article key={rule.id} className={`fm-card fm-rule${rule.enabled ? '' : ' is-off'}`}>
              <header className="fm-rule-head">
                <span className="fm-rule-icon">
                  <FormIcon name={action.icon} size={17} />
                </span>
                <input className="fm-rule-name" value={rule.name} placeholder={`Automation ${index + 1}`} onChange={(e) => update(rule.id, { name: e.target.value })} aria-label="Automation name" />
                <Toggle checked={rule.enabled} onChange={(v) => update(rule.id, { enabled: v })} label={rule.enabled ? 'On' : 'Off'} />
                <button type="button" className="fm-icon-btn is-danger" onClick={() => setRules((list) => list.filter((r) => r.id !== rule.id))} aria-label="Delete automation">
                  <FormIcon name="trash" size={16} />
                </button>
              </header>
              <div className="fm-rule-flow">
                <Field label="When">
                  <div className="fm-select-wrap">
                    <SearchableSelect value={rule.trigger} onChange={(e) => update(rule.id, { trigger: e.target.value })}>
                      {TRIGGERS.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </SearchableSelect>
                    <FormIcon name="chevronDown" size={16} />
                  </div>
                </Field>
                <span className="fm-rule-arrow" aria-hidden="true">
                  <FormIcon name="chevronRight" size={18} />
                </span>
                <Field label="Then">
                  <div className="fm-select-wrap">
                    <SearchableSelect value={rule.action} onChange={(e) => update(rule.id, { action: e.target.value })}>
                      {ACTIONS.map((a) => (
                        <option key={a.id} value={a.id} disabled={a.id === 'ghl_tags' && !options.ghl_connected}>
                          {a.label}
                          {a.id === 'ghl_tags' && !options.ghl_connected ? ' (not connected)' : ''}
                        </option>
                      ))}
                    </SearchableSelect>
                    <FormIcon name="chevronDown" size={16} />
                  </div>
                </Field>
              </div>

              <div className="fm-rule-config">
                {rule.action === 'create_task' ? (
                  <div className="fm-grid-3">
                    <Field label="Task title">
                      <input value={rule.config.title} onChange={(e) => updateConfig(rule.id, { title: e.target.value })} />
                    </Field>
                    <Field label="Assign to">
                      <ClearableSelect value={rule.config.assignee_id} placeholder="Unassigned" options={options.staff || []} onChange={(v) => updateConfig(rule.id, { assignee_id: v })} />
                    </Field>
                    <Field label="Due in (days)" hint="0 = no due date">
                      <input type="number" min={0} max={60} value={rule.config.due_in_days} onChange={(e) => updateConfig(rule.id, { due_in_days: Number(e.target.value) || 0 })} />
                    </Field>
                  </div>
                ) : null}
                {rule.action === 'send_email' || rule.action === 'notify_team' ? (
                  <>
                    {rule.action === 'send_email' ? (
                      <Field label="To" hint="Comma-separated. Empty = the respondent's email.">
                        <ListInput value={rule.config.to} onChange={(v) => updateConfig(rule.id, { to: v })} placeholder="sales@agency.com" />
                      </Field>
                    ) : null}
                    <Field label={rule.action === 'notify_team' ? 'Title' : 'Subject'}>
                      <input value={rule.config.subject} placeholder="{{form_name}}" onChange={(e) => updateConfig(rule.id, { subject: e.target.value })} />
                    </Field>
                    <Field label="Message">
                      <textarea rows={4} value={rule.config.body} placeholder="{{answers}}" onChange={(e) => updateConfig(rule.id, { body: e.target.value })} />
                    </Field>
                  </>
                ) : null}
                {rule.action === 'webhook' ? (
                  <Field label="Webhook URL" hint="We POST JSON with the form, submission, answers and event (form.submitted / form.reviewed).">
                    <input type="url" value={rule.config.url} placeholder="https://hooks.zapier.com/…" onChange={(e) => updateConfig(rule.id, { url: e.target.value })} />
                  </Field>
                ) : null}
                {rule.action === 'ghl_tags' ? (
                  <Field label="Tags" hint="Creates / updates the contact, then applies these tags.">
                    <ListInput value={rule.config.tags} onChange={(v) => updateConfig(rule.id, { tags: v })} placeholder="hot-lead, discovery-complete" />
                  </Field>
                ) : null}
              </div>
            </article>
          );
        })}
        {rules.length ? <p className="fm-hint">Remember to Save. Each run is logged on the submission so you can see what happened.</p> : null}
      </section>
      <aside>
        <Builtins draft={draft} goSettings={goSettings} />
      </aside>
    </div>
  );
}
